function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { createLink } from "../../utils";
import queryString from "query-string";
import { useGlobalValue } from "../../state/GlobalProvider";
import { SovosDataGridNext, SovosToolbarActions, SovosToolbarGroup } from "mosaic-react";
import styles from "./DataGrid.module.sass";
import { parseSort, stringifySort, parseFilter } from "../../utils/queryUtils";
import ToolbarParent from "./ToolbarParent";
import DefaultToolbar from "./DefaultToolbar";
import { getCard, buildAvailableFilters, buildColumns, buildRows } from "./utils";
import DatagridFilters from "../DatagridFilters/DatagridFilters";
import { useGridApiRef } from "@mui/x-data-grid";
import { buildColumnTotalizers } from "./utils/utils";
const DataGrid = props => {
  const {
    id,
    basePath,
    items: listItems = [],
    header,
    headerHeight,
    card,
    cardKey,
    metadata,
    page,
    pageSize,
    status = {
      loading: true
    },
    availableFilters,
    sort,
    showDetailPanel,
    showCheckbox,
    toolbar,
    toolbarProps,
    filter,
    rowSelection,
    totalizers,
    onRowClickCustom,
    onRowEdit = () => {},
    editMode = false,
    enablePagination = true,
    modifierssx = {},
    onCellHighlight = () => ""
  } = props;
  const {
    i18n,
    params,
    location,
    history,
    proxy,
    detailPanelComponents
  } = useGlobalValue();
  const queryParams = queryString.parse(location.search);
  const filterArray = filter ? parseFilter(filter) : [];
  const [columnTotalizer, setColumnTotalizer] = useState([]);
  const [columnsToHide, setColumnsToHide] = useState({});
  const [isPreferencesPanelOpen, setIsPreferencesPanelOpen] = useState(false);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [rowsSelected, setRowsSelected] = useState(0);
  const gridRef = useGridApiRef();
  const sortRef = useRef(sort);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: queryParams?.pageSize && parseInt(queryParams?.pageSize) || pageSize && parseInt(pageSize) || 20,
    page: queryParams?.page && parseInt(queryParams?.page) || page && parseInt(page) || 0
  });
  const rowCountRef = useRef(metadata?.total || 0);
  const availableFiltersRef = useRef(availableFilters || []);
  const headerRef = useRef(header || []);
  const totalizerRef = useRef(totalizers || {});
  const columnsRef = useRef([]);
  const Card = getCard(card, cardKey);
  const rows = buildRows(listItems, header);
  const getDetailPanelContent = useCallback(({
    row
  }) => {
    if (!row?.detailPanel || !row.detailPanel?.detailPanelComponent || !row.detailPanel?.data || !detailPanelComponents || !detailPanelComponents[row.detailPanel.detailPanelComponent]) {
      return null;
    }
    const DetailPanelComponent = detailPanelComponents[row.detailPanel.detailPanelComponent];
    return /*#__PURE__*/React.createElement(DetailPanelComponent, row);
  }, []);
  const isClickable = rows?.[0]?.link?.to;
  const onPreferencePanelClose = params => {
    params.openedPanelValue === "columns" ? setIsPreferencesPanelOpen(false) : setIsFilterPanelOpen(false);
  };
  const onPreferencePanelOpen = params => {
    if (params.openedPanelValue === "columns") {
      setIsPreferencesPanelOpen(true);
      setIsFilterPanelOpen(false);
    } else {
      setIsFilterPanelOpen(true);
      setIsPreferencesPanelOpen(false);
    }
  };
  const rowCount = useMemo(() => {
    if (metadata?.total !== undefined) {
      rowCountRef.current = metadata.total;
    }
    return rowCountRef.current;
  }, [metadata?.total]);
  const availableFilterMemoized = useMemo(() => {
    if (!status?.loading && JSON.stringify(availableFilters || []) !== JSON.stringify(availableFiltersRef.current)) {
      availableFiltersRef.current = availableFilters;
    }
    return availableFiltersRef.current;
  }, [availableFilters]);
  const headerMemoized = useMemo(() => {
    if (header && JSON.stringify(header || []) !== JSON.stringify(headerRef.current)) {
      headerRef.current = header;
    }
    return headerRef.current;
  }, [header]);
  const totalizersMemoized = useMemo(() => {
    if (totalizers && JSON.stringify(totalizers || {}) !== JSON.stringify(totalizerRef.current)) {
      totalizerRef.current = totalizers;
    }
    return totalizerRef.current;
  }, [totalizers]);
  const onPaginationModelChange = paginationModel => {
    const queryParams = queryString.parse(location.search);
    setPaginationModel({
      page: paginationModel.page,
      pageSize: paginationModel.pageSize
    });
    history.replace({
      search: queryString.stringify({
        ...queryParams,
        page: paginationModel.page,
        pageSize: paginationModel.pageSize
      })
    });
  };
  const sortMemoized = useMemo(() => {
    const parsedSort = (parseSort(sort) || []).map(({
      column,
      order
    }) => ({
      field: column,
      sort: order
    }));
    if (!status?.loading && JSON.stringify(parsedSort || []) !== JSON.stringify(sortRef.current)) {
      sortRef.current = parsedSort;
      return parsedSort;
    }
  }, [sort]);
  const onSortModelChange = sortModelChange => {
    const newSort = sortModelChange.map(sort => ({
      column: sort.field,
      order: sort.sort
    }));
    if (!status?.loading && JSON.stringify(sortModelChange) !== JSON.stringify(sortMemoized)) {
      const queryParams = queryString.parse(location.search);
      history.replace({
        search: queryString.stringify({
          ...queryParams,
          sort: stringifySort(newSort)
        })
      });
    }
  };
  const onRowClick = clickParams => {
    if (clickParams) {
      const path = createLink(basePath, proxy, clickParams.row, {
        ...params
      });
      path && history.push(path);
    }
  };
  const columnMemoized = useMemo(() => {
    const columnsObj = buildColumns(headerMemoized, Card, i18n, editMode);
    if (headerMemoized?.length && Object.keys(totalizersMemoized || {}).length) {
      setColumnTotalizer(buildColumnTotalizers(columnsObj, totalizersMemoized, i18n));
    }
    if (availableFilterMemoized?.length) {
      const {
        columnsWithFilters
      } = buildAvailableFilters(availableFilterMemoized, columnsObj, i18n);
      if (JSON.stringify(columnsWithFilters) !== JSON.stringify(columnsRef.current)) {
        columnsRef.current = Object.values(columnsWithFilters || {});
      }
      return columnsRef.current;
    }
    const newColumns = Object.values(columnsObj || {});
    if (JSON.stringify(newColumns) !== JSON.stringify(columnsRef.current)) {
      columnsRef.current = newColumns;
    }
    return columnsRef.current;
  }, [availableFilterMemoized, headerMemoized, totalizersMemoized, Card, i18n, editMode]);
  const pointerStyles = isClickable ? {
    cursor: "pointer"
  } : {};
  const getTogglableColumns = gridColumns => {
    return gridColumns.filter(column => !["__check__", "__detail_panel_toggle__"].includes(column.field)).map(column => column.field);
  };
  return /*#__PURE__*/React.createElement("div", {
    id: id,
    className: `${styles["root"]}`
  }, /*#__PURE__*/React.createElement(SovosDataGridNext, _extends({
    sx: {
      ...modifierssx,
      ...pointerStyles,
      zIndex: "0",
      position: "relative",
      left: "0",
      top: "0",
      width: "100%",
      "& .MuiDataGrid-cell": {
        display: "flex",
        alignItems: "center"
      },
      "& .bold-underline-row": {
        borderBottom: "1px solid #000"
      },
      "& .bold-underline-row *": {
        fontWeight: 1000
      }
    },
    loading: status?.loading,
    columnVisibilityModel: {
      ...columnsToHide
    },
    sortingMode: "server",
    sortModel: sortMemoized,
    onColumnVisibilityModelChange: newModel => setColumnsToHide(newModel),
    autoHeight: rows?.length > 0 && rows?.length <= paginationModel.pageSize,
    disableColumnResize: false,
    columns: columnMemoized || [],
    rows: rows || [],
    paginationMode: "server",
    rowCount: rowCount,
    pageSizeOptions: rows?.length > 0 ? [20, 50, 100] : [],
    paginationModel: paginationModel,
    pagination: enablePagination,
    slots: {
      toolbar: ToolbarParent,
      filterPanel: DatagridFilters
    },
    slotProps: {
      toolbar: {
        ToolbarContent: toolbar ? toolbar : () => {
          return /*#__PURE__*/React.createElement(SovosToolbarGroup, {
            sx: {
              width: "100%"
            }
          }, /*#__PURE__*/React.createElement(SovosToolbarActions, null, /*#__PURE__*/React.createElement(DefaultToolbar, {
            isPreferencesPanelOpen: isPreferencesPanelOpen,
            isFilterPanelOpen: isFilterPanelOpen,
            availableFilters: availableFilters,
            filterArray: filterArray
          })));
        },
        toolbarProps: {
          ...toolbarProps,
          availableFilters: availableFilterMemoized,
          filterArray,
          rowsSelected
        },
        defaultToolbarProps: {
          isPreferencesPanelOpen,
          isFilterPanelOpen,
          availableFilters: availableFilterMemoized,
          filterArray
        }
      },
      filterPanel: {
        availableFilters: availableFilterMemoized,
        filter,
        anchor: "results",
        filterArray
      },
      panel: {
        placement: "bottom-end"
      },
      columnsManagement: {
        getTogglableColumns
      }
    },
    filterMode: "server",
    onSortModelChange: onSortModelChange,
    onRowClick: onRowClickCustom || onRowClick,
    onPreferencePanelOpen: onPreferencePanelOpen,
    onPreferencePanelClose: onPreferencePanelClose,
    onPaginationModelChange: onPaginationModelChange,
    getDetailPanelContent: showDetailPanel ? getDetailPanelContent : null,
    getDetailPanelHeight: () => "auto",
    checkboxSelection: showCheckbox,
    isRowSelectable: rowSelection,
    disableColumnMenu: false,
    apiRef: gridRef,
    columnGroupingModel: columnTotalizer,
    getRowId: row => {
      return row?.uniqueId || row?.id;
    }
  }, rows?.length > 0 && {
    getRowClassName: ({
      row
    }) => {
      return row?.groupingLine ? "bold-underline-row" : "";
    }
  }, {
    processRowUpdate: onRowEdit,
    editModeCell: "cell",
    onProcessRowUpdateError: error => console.log(error),
    columnHeaderHeight: headerHeight,
    getCellClassName: onCellHighlight,
    onRowSelectionModelChange: (rowSelectionModel, callbackDetails) => {
      setRowsSelected((rowSelectionModel || []).map(rowId => callbackDetails.api.getRow(rowId)));
    },
    disableVirtualization: !(rows?.length > 0 && rows?.length <= paginationModel.pageSize)
  })));
};
export default DataGrid;